import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NotificationsService, ProjectService } from '@catcore/services';
import { FileBlob } from '@catcore/types/base';
import { extractDataFromModel } from '@catcore/utils';
import { ProjectServiceFilesService } from '@catmodules/models/project-service-files/services/project-service-files.service';
import { ProjectServiceFiles, ProjectServiceFilesTableItem } from '@catmodules/models/project-service-files/types';
import { InputTextComponent } from '@catuiform/components/input-text/input-text.component';
import * as FileSaver from 'file-saver';
import * as moment from 'moment';
import { Subscription } from 'rxjs/Subscription';
import { isNullOrUndefined } from 'util';
import {ServerEventService} from "@catcore/services/server-event.service";

@Component({
  selector: 'cat-project-service-files',
  templateUrl: './project-service-files.component.html',
  styleUrls: ['./project-service-files.component.scss'],
})

export class ProjectServiceFilesComponent implements OnInit, OnDestroy {

  private serviceFilesSubscription: Subscription;
  private csdbFilesSubscription: Subscription;
  private projectSubscription: Subscription;
  private serverEventSubscription: Subscription;
  private project = null;

  @ViewChild('versionInput')
  versionInput: InputTextComponent;

  private serviceFiles = null;
  private csdbFiles = null;

  tableItems: ProjectServiceFilesTableItem[] = [];
  private serviceFilesInitialized = false;
  private csdbFilesInitialized = false;
  dataTableInitialized = false;

  columns = {
    unit: { field: 'unit', header: 'Unit' },
    pn: { field: 'pn', header: 'Part Number' },
    location: { field: 'location', header: 'Location ID' },
    service: { field: 'service', header: 'Service' },
    filename: { field: 'filename', header: 'Filename' },
    version: { field: 'formatted_version', header: 'Version' },
    date: { field: 'date', header: 'Date' },
    size: { field: 'size', header: 'Size' },
    md5: { field: 'md5', header: 'MD5' },
    description: { field: 'description', header: 'Description' },
  };

  private displayEditVersionDialog = false;
  private currentConfig = null;
  private currentTableItem: ProjectServiceFilesTableItem;

  constructor(private projectService: ProjectService,
    private projectServiceFilesService: ProjectServiceFilesService,
    private notificationService: NotificationsService,
    private serverEventService: ServerEventService) { }

  private static dateToString(date: moment.MomentInput) {
    return moment(date).format('YYYY-MM-DD');
  }

  dataAvailable() {
    return !isNullOrUndefined(this.tableItems) && this.tableItems.length !== 0;
  }

  ngOnInit() {
    // first get the current project
    this.projectSubscription = this.projectService.getCurrentProject()
      .subscribe((project) => {
        const isActiveProject = !isNullOrUndefined(project) && !isNullOrUndefined(project.id);
        if (isActiveProject) {
          this.project = project;
          this.projectServiceFilesService.setActive();
          this.updateTable();
        }
      });

    this.serverEventSubscription = this.serverEventService.getServerEvent().subscribe((event) => {
      if (event === this.serverEventService.EVENT_CONFIG_CREATION_FINISHED) {
        this.updateTable();
      }
    })
  }

  ngOnDestroy() {
    if (this.csdbFilesSubscription) {
      this.csdbFilesSubscription.unsubscribe();
    }
    if (this.serviceFilesSubscription) {
      this.serviceFilesSubscription.unsubscribe();
    }
    this.projectSubscription.unsubscribe();
    this.serverEventSubscription.unsubscribe();

    this.projectServiceFilesService.setInActive();
  }

  protected updateTable() {
    this.dataTableInitialized = false;
    this.csdbFilesInitialized = false;
    this.serviceFilesInitialized = false;

    this.serviceFiles = null;
    this.csdbFiles = null;

    if (this.serviceFilesSubscription) {
      this.serviceFilesSubscription.unsubscribe();
    }
    this.serviceFilesSubscription = this.projectService.getProjectServiceFiles(this.project)
      .subscribe((projectServiceFiles: ProjectServiceFiles) => {
        this.serviceFilesInitialized = true;
        this.serviceFiles = (projectServiceFiles._embedded ? projectServiceFiles._embedded.item : []);
        if (this.csdbFilesInitialized && this.serviceFilesInitialized) {
          this.createDataTableItems(this.serviceFiles, this.csdbFiles);
        }
      });

    if (this.csdbFilesSubscription) {
      this.csdbFilesSubscription.unsubscribe();
    }
    this.csdbFilesSubscription = this.projectService.getCsdbConfigFiles(this.project)
      .subscribe((csdbConfigFiles) => {
        this.csdbFilesInitialized = true;
        this.csdbFiles = csdbConfigFiles;
        if (this.csdbFilesInitialized && this.serviceFilesInitialized) {
          this.createDataTableItems(this.serviceFiles, this.csdbFiles);
        }
      });
  }

  downloadFile(serviceOrUnitConfig: { filename: any; }) {
    const subscription = this.projectServiceFilesService.downloadFile(serviceOrUnitConfig)
      .finally(() => subscription.unsubscribe())
      .subscribe((fileBlob: FileBlob) => {
        const defaultFileName = `${serviceOrUnitConfig.filename}`;
        FileSaver.saveAs(fileBlob.blob, fileBlob.filename ? fileBlob.filename : defaultFileName);
      });
  }


  private createDataTableItems(serviceFiles: any[], csdbFiles: any) {
    this.tableItems = [];

    const keys = ['aircraft_xml', 'lru_definition', 'app_definition'];
    keys.forEach((key) => {
      const csdbConfig = (csdbFiles._embedded && csdbFiles._embedded.hasOwnProperty(key)) ? csdbFiles._embedded[key] : null;
      if (csdbConfig !== null) {
        const data: ProjectServiceFilesTableItem = {
          service_config: null,
          unit_config: null,
          service: 'CSDB',
          unit: '',
          pn: '',
          location: '',
          filename: csdbConfig.description,
          version: csdbConfig.version,
          formatted_version: csdbConfig.version + (csdbConfig.version > 99 ? ` (${this.intVersionToStr(csdbConfig.version)})` : ''),
          date: ProjectServiceFilesComponent.dateToString(csdbConfig.date),
          size: csdbConfig.human_readable_file_size,
          md5: '',
          version_editable: csdbConfig.version_editable,
          description: csdbConfig.description,
          clickHandler: () => { this.downloadFile(csdbConfig); },
          onEditVersion: () => {}
        };
        this.tableItems = [...this.tableItems, data];
      }
    });

    serviceFiles.forEach((unit) => {

      if (unit._embedded && unit._embedded.hasOwnProperty('unit_config')) {
        const unitConfig = unit._embedded.unit_config;
        const data: ProjectServiceFilesTableItem = {
          service_config: null,
          unit_config: unitConfig,
          service: 'LRU',
          unit: unit.name,
          pn: unit.part_number,
          location: unit.location_address,
          filename: unitConfig.filename,
          version: unitConfig.version,
          formatted_version: unitConfig.version + (unitConfig.version > 99 ? ` (${this.intVersionToStr(unitConfig.version)})` : ''),
          date: ProjectServiceFilesComponent.dateToString(unitConfig.date),
          size: unitConfig.human_readable_file_size,
          md5: unitConfig.md5,
          version_editable: unitConfig.version_editable,
          description: unitConfig.description,
          clickHandler: () => { this.downloadFile(unitConfig); },
          onEditVersion: () => {
            this.versionInput.writeValue(data.version);
            this.displayEditVersionDialog = true;
            this.currentConfig = unitConfig;
            this.currentTableItem = data;
          },
        };
        this.tableItems = [...this.tableItems, data];
      }

      extractDataFromModel(unit, 'services', '_embedded').forEach((service: { name: any; }) => {
        const srvcCfgFiles = extractDataFromModel(service, 'service_configs', '_embedded');
        const linkedSrvcCfgFiles = extractDataFromModel(service, 'linked_service_configs', '_embedded');
        [...srvcCfgFiles, ...linkedSrvcCfgFiles].forEach((cfg) => {
          const data: ProjectServiceFilesTableItem = {
            service_config: cfg,
            unit_config: null,
            service: service.name,
            unit: unit.name,
            pn: unit.part_number,
            location: unit.location_address,
            filename: cfg.filename,
            version: cfg.version,
            formatted_version: cfg.version + (cfg.version > 99 ? ` (${this.intVersionToStr(cfg.version)})` : ''),
            date: ProjectServiceFilesComponent.dateToString(cfg.date),
            size: cfg.human_readable_file_size,
            md5: cfg.md5,
            version_editable: cfg.version_editable,
            description: cfg.description,
            clickHandler: () => { this.downloadFile(cfg); },
            onEditVersion: () => {
              this.versionInput.writeValue(data.version);
              this.displayEditVersionDialog = true;
              this.currentConfig = cfg;
              this.currentTableItem = data;
            },
          };
          this.tableItems = [...this.tableItems, data];
        });
      });
    });
    this.dataTableInitialized = true;
  }

  private intVersionToStr(version: number) {
    if (version <= 99) {
      return version.toString();
    }
    const ch1 = (Math.trunc(((version - 100) / 36)) + 10).toString(36);
    const ch2 = ((version - 100) % 36).toString(36);
    return (ch1 + ch2).toUpperCase();
  }

  private updateVersion(newVersion: number) {
    // The user selected the new version, we can close the popup.
    this.displayEditVersionDialog = false;

    this.projectService
      .editProjectServiceFileVersion(newVersion, this.currentConfig._links.self.href)
      .subscribe(() => {
        // Change the version in the UI. Even though we change the version for one row,
        // one file may be referenced in other rows as well.
        this.tableItems.forEach((tableItem) => {
          // Search for all files with the same md5, they all need to have the same version.
          if (tableItem.md5 === this.currentConfig.md5) {
            // Update version for the UI table
            tableItem.formatted_version = newVersion + (newVersion > 99 ? ` (${this.intVersionToStr(newVersion)})` : '');
            tableItem.version = newVersion;
            // We need as well to change the version for this object.
            // It is used when showing the popup.
            if (tableItem.unit_config) {
              tableItem.unit_config.formatted_version = newVersion + (newVersion > 99 ? ` (${this.intVersionToStr(newVersion)})` : '');
              tableItem.unit_config.version = newVersion;
            }
          }
        });

        this.notificationService.success(
          'Successful version change',
          `${this.currentConfig.filename} is now version ${newVersion} ${(newVersion > 99 ? `(${this.intVersionToStr(newVersion)})` : '')}`,
        );
        this.currentConfig = null;
        this.currentTableItem = null;
      }, (error) => {
        this.currentConfig = null;
        this.currentTableItem = null;
        console.error('version change failed', error);
      });
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/models/project-service-files/components/project-service-files/project-service-files.component.ts